import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack } from '@mui/material';
import { ConversationSettingsConfig, conversationSettingsConfigSchema } from '@procureai/agents';
import { FormSchemaProvider, FormSchemaProviderProps } from '@procureai/ui-form-mui';
import { ConversationSettingsConfigForm } from '@procureai/ui-modules-mui';
import { useUuid } from '@procureai/ui-mui';
import { useTranslation } from 'react-i18next';

import { I18nNamespace } from '~/common/types';
import { DialogActionsLayout } from '~/components/DialogWizard/components/DialogActionsLayout/DialogActionsLayout';

export interface ConversationSettingsFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: FormSchemaProviderProps<ConversationSettingsConfig>['onSubmit'];
  data?: ConversationSettingsConfig;
  defaultValues: Partial<ConversationSettingsConfig>;
  loading?: boolean;
  submitting?: boolean;
}

export const ConversationSettingsFormDialog = ({
  open,
  onClose,
  onSave,
  data,
  defaultValues,
  loading = false,
  submitting = false,
}: ConversationSettingsFormDialogProps) => {
  const formId = useUuid();
  const { t } = useTranslation(I18nNamespace.conversation, { keyPrefix: 'ConversationSettingsConfigForm' });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>{t('title')}</DialogTitle>
      <DialogContent>
        <FormSchemaProvider<ConversationSettingsConfig>
          id={formId}
          schema={conversationSettingsConfigSchema}
          defaultValues={defaultValues}
          onSubmit={onSave}
          data={data}
        >
          <Stack direction="column" spacing={4}>
            <ConversationSettingsConfigForm />
          </Stack>
        </FormSchemaProvider>
      </DialogContent>
      <DialogActions>
        <DialogActionsLayout
          right={
            <>
              <Button onClick={onClose} disabled={loading || submitting} type="button" variant="outlined">
                {t('actions.cancel')}
              </Button>
              <Button
                disabled={loading || submitting}
                loading={submitting}
                type="submit"
                variant="contained"
                color="primary"
                form={formId}
              >
                {t('actions.done')}
              </Button>
            </>
          }
        />
      </DialogActions>
    </Dialog>
  );
};
